import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Notification from '../components/common/Notification';
import ConfirmModal from '../components/common/ConfirmModal';
import { getApiBaseUrl } from '../utils/getApiUrl';
import { HIS_MENU_TITLE } from '../config/hisSections';

interface AdminUser {
  id: number;
  username: string;
  email: string;
  role: string;
  is_active: boolean;
  management_report_access?: boolean;
  complaints_access?: boolean;
  monitoring_access?: boolean;
  ami_access?: boolean;
  call_center_report_access?: boolean;
  his_report_access?: boolean;
  screen_pop_access?: boolean;
  customer_club_access?: boolean;
  customer_club_segments_access?: boolean;
  phone_extension?: string | null;
  created_at?: string;
  updated_at?: string;
}

type PermissionKey =
  | 'management_report_access'
  | 'complaints_access'
  | 'monitoring_access'
  | 'ami_access'
  | 'call_center_report_access'
  | 'his_report_access'
  | 'customer_club_access'
  | 'customer_club_segments_access'
  | 'screen_pop_access';

const PERMISSION_DEFS: { key: PermissionKey; label: string; skipForSuperAdmin?: boolean }[] = [
  { key: 'management_report_access', label: 'گزارش', skipForSuperAdmin: true },
  { key: 'complaints_access', label: 'انتقادات', skipForSuperAdmin: true },
  { key: 'monitoring_access', label: 'مانیتورینگ', skipForSuperAdmin: true },
  { key: 'ami_access', label: 'AMI', skipForSuperAdmin: true },
  { key: 'call_center_report_access', label: 'گزارش تماس', skipForSuperAdmin: true },
  { key: 'his_report_access', label: HIS_MENU_TITLE, skipForSuperAdmin: true },
  { key: 'customer_club_access', label: 'باشگاه مشتریان', skipForSuperAdmin: true },
  { key: 'customer_club_segments_access', label: 'تنظیم سگمنت‌ها', skipForSuperAdmin: true },
  { key: 'screen_pop_access', label: 'پاپ‌آپ تماس' },
];

const isSuperAdminRole = (role: string) => role === 'admin' || role === 'superadmin';

const getActivePermissions = (user: AdminUser) => {
  if (isSuperAdminRole(user.role)) {
    return PERMISSION_DEFS.filter((p) => !p.skipForSuperAdmin && Boolean(user[p.key]));
  }
  return PERMISSION_DEFS.filter((p) => Boolean(user[p.key]));
};

const formatCreatedAt = (createdAt?: string) => {
  if (!createdAt) return '—';
  try {
    return new Date(createdAt).toLocaleDateString('fa-IR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return '—';
  }
};

const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'info' | 'warning' | 'error' } | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Form states
  const [newUsername, setNewUsername] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('expert');
  const [newIsActive, setNewIsActive] = useState(true);

  const [editUsername, setEditUsername] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editRole, setEditRole] = useState('user');
  const [editIsActive, setEditIsActive] = useState(true);
  const [editManagementReportAccess, setEditManagementReportAccess] = useState(false);
  const [newManagementReportAccess, setNewManagementReportAccess] = useState(false);
  const [editComplaintsAccess, setEditComplaintsAccess] = useState(false);
  const [newComplaintsAccess, setNewComplaintsAccess] = useState(false);
  const [editMonitoringAccess, setEditMonitoringAccess] = useState(false);
  const [newMonitoringAccess, setNewMonitoringAccess] = useState(false);
  const [editAmiAccess, setEditAmiAccess] = useState(false);
  const [newAmiAccess, setNewAmiAccess] = useState(false);
  const [editCallCenterReportAccess, setEditCallCenterReportAccess] = useState(false);
  const [newCallCenterReportAccess, setNewCallCenterReportAccess] = useState(false);
  const [editHisReportAccess, setEditHisReportAccess] = useState(false);
  const [newHisReportAccess, setNewHisReportAccess] = useState(false);
  const [editScreenPopAccess, setEditScreenPopAccess] = useState(false);
  const [newScreenPopAccess, setNewScreenPopAccess] = useState(false);
  const [editCustomerClubAccess, setEditCustomerClubAccess] = useState(false);
  const [newCustomerClubAccess, setNewCustomerClubAccess] = useState(false);
  const [editCustomerClubSegmentsAccess, setEditCustomerClubSegmentsAccess] = useState(false);
  const [newCustomerClubSegmentsAccess, setNewCustomerClubSegmentsAccess] = useState(false);

  const viewerRole = (localStorage.getItem('admin_role') || '').trim().toLowerCase();
  const isMainAdminViewer = viewerRole === 'admin';

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const role = viewerRole;

    if (!token) {
      navigate('/admin/login');
      return;
    }

    if (role !== 'admin') {
      navigate('/admin/dashboard');
      return;
    }

    fetchUsers();
  }, [navigate]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('شما دسترسی به این بخش را ندارید');
        }
        throw new Error('خطا در دریافت لیست کاربران');
      }

      const data = await response.json();
      setUsers(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUsername.trim() || !newEmail.trim() || !newPassword.trim()) {
      setNotification({ message: 'لطفاً تمام فیلدهای الزامی را پر کنید', type: 'warning' });
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/admin/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: newUsername.trim(),
          email: newEmail.trim(),
          password: newPassword,
          role: newRole,
          is_active: newIsActive,
          ...(isMainAdminViewer ? { management_report_access: newManagementReportAccess } : {}),
          ...(isMainAdminViewer ? { complaints_access: newComplaintsAccess } : {}),
          ...(isMainAdminViewer ? { monitoring_access: newMonitoringAccess } : {}),
          ...(isMainAdminViewer ? { ami_access: newAmiAccess } : {}),
          ...(isMainAdminViewer ? { call_center_report_access: newCallCenterReportAccess } : {}),
          ...(isMainAdminViewer ? { his_report_access: newHisReportAccess } : {}),
          ...(isMainAdminViewer ? { screen_pop_access: newScreenPopAccess } : {}),
          ...(isMainAdminViewer ? { customer_club_access: newCustomerClubAccess } : {}),
          ...(isMainAdminViewer
            ? { customer_club_segments_access: newCustomerClubSegmentsAccess }
            : {}),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'خطا در ایجاد کاربر');
      }

      // Reset form
      setNewUsername('');
      setNewEmail('');
      setNewPassword('');
      setNewRole('expert');
      setNewIsActive(true);
      setNewManagementReportAccess(false);
      setNewComplaintsAccess(false);
      setNewMonitoringAccess(false);
      setNewAmiAccess(false);
      setNewCallCenterReportAccess(false);
      setNewHisReportAccess(false);
      setNewScreenPopAccess(false);
      setNewCustomerClubAccess(false);
      setNewCustomerClubSegmentsAccess(false);
      setShowAddModal(false);
      fetchUsers();
      setNotification({ message: 'کاربر با موفقیت اضافه شد', type: 'success' });
    } catch (err: any) {
      setError(err.message);
      setNotification({ message: err.message || 'خطا در ایجاد کاربر', type: 'error' });
    }
  };

  const handleEditUser = (user: AdminUser) => {
    setEditingUser(user);
    setEditUsername(user.username);
    setEditEmail(user.email);
    setEditPassword('');
    setEditRole(user.role);
    setEditIsActive(user.is_active);
    setEditManagementReportAccess(Boolean(user.management_report_access));
    setEditComplaintsAccess(Boolean(user.complaints_access));
    setEditMonitoringAccess(Boolean(user.monitoring_access));
    setEditAmiAccess(Boolean(user.ami_access));
    setEditCallCenterReportAccess(Boolean(user.call_center_report_access));
    setEditHisReportAccess(Boolean(user.his_report_access));
    setEditScreenPopAccess(Boolean(user.screen_pop_access));
    setEditCustomerClubAccess(Boolean(user.customer_club_access));
    setEditCustomerClubSegmentsAccess(Boolean(user.customer_club_segments_access));
    setShowEditModal(true);
  };

  const handleUpdateUser = async () => {
    if (!editUsername.trim() || !editEmail.trim() || !editingUser) {
      setNotification({ message: 'لطفاً تمام فیلدهای الزامی را پر کنید', type: 'warning' });
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();
      const updateData: Record<string, unknown> = {
        username: editUsername.trim(),
        email: editEmail.trim(),
        role: editRole,
        is_active: editIsActive,
      };

      // فقط اگر رمز عبور جدید وارد شده باشد، آن را اضافه می‌کنیم
      if (editPassword.trim()) {
        updateData.password = editPassword;
      }
      if (isMainAdminViewer) {
        updateData.management_report_access = editManagementReportAccess;
        updateData.complaints_access = editComplaintsAccess;
        updateData.monitoring_access = editMonitoringAccess;
        updateData.ami_access = editAmiAccess;
        updateData.call_center_report_access = editCallCenterReportAccess;
        updateData.his_report_access = editHisReportAccess;
        updateData.screen_pop_access = editScreenPopAccess;
        updateData.customer_club_access = editCustomerClubAccess;
        updateData.customer_club_segments_access = editCustomerClubSegmentsAccess;
      }

      const response = await fetch(`${apiBaseUrl}/admin/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'خطا در به‌روزرسانی کاربر');
      }

      setShowEditModal(false);
      setEditingUser(null);
      fetchUsers();
      setNotification({ message: 'کاربر با موفقیت به‌روزرسانی شد', type: 'success' });
    } catch (err: any) {
      setError(err.message);
      setNotification({ message: err.message || 'خطا در به‌روزرسانی کاربر', type: 'error' });
    }
  };

  const handleDeleteUser = (userId: number) => {
    setUserToDelete(userId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/admin/users/${userToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'خطا در حذف کاربر');
      }

      setShowDeleteConfirm(false);
      setUserToDelete(null);
      fetchUsers();
      setNotification({ message: 'کاربر با موفقیت حذف شد', type: 'success' });
    } catch (err: any) {
      setError(err.message);
      setNotification({ message: err.message || 'خطا در حذف کاربر', type: 'error' });
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    }
  };

  const getRoleLabel = (role: string) => {
    const roles: { [key: string]: string } = {
      'admin': 'مدیر اصلی',
      'superadmin': 'مدیر اصلی',
      'manager': 'مدیر',
      'expert': 'کارشناس',
      'user': 'کاربر'
    };
    return roles[role] || role;
  };

  const getRoleBadgeColor = (role: string) => {
    const colors: { [key: string]: string } = {
      'admin': 'bg-red-100 text-red-800',
      'superadmin': 'bg-red-100 text-red-800',
      'manager': 'bg-purple-100 text-purple-800',
      'expert': 'bg-blue-100 text-blue-800',
      'user': 'bg-gray-100 text-gray-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const filteredUsers = users.filter((user) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      user.username.toLowerCase().includes(q) ||
      user.email.toLowerCase().includes(q) ||
      getRoleLabel(user.role).includes(q)
    );
  });

  const renderPermissionBadges = (user: AdminUser, compact = false) => {
    if (isSuperAdminRole(user.role)) {
      return (
        <span className="inline-flex rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
          دسترسی کامل
        </span>
      );
    }

    const active = getActivePermissions(user);
    if (active.length === 0) {
      return <span className="text-xs text-gray-400">بدون دسترسی اضافه</span>;
    }

    return (
      <div className={`flex flex-wrap gap-1 ${compact ? '' : 'max-w-md'}`}>
        {active.map((p) => (
          <span
            key={p.key}
            className="inline-flex rounded-md bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-800 ring-1 ring-emerald-100"
          >
            {p.label}
          </span>
        ))}
      </div>
    );
  };

  const renderUserActions = (user: AdminUser) => (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => handleEditUser(user)}
        className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
      >
        ویرایش
      </button>
      <button
        type="button"
        onClick={() => handleDeleteUser(user.id)}
        className="rounded-lg border border-red-100 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
      >
        حذف
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner text="در حال بارگذاری کاربران..." />
      </div>
    );
  }

  if (error && error.includes('دسترسی')) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                {error}
              </h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="حذف کاربر"
        message="آیا از حذف این کاربر اطمینان دارید؟"
        onConfirm={confirmDeleteUser}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setUserToDelete(null);
        }}
        confirmText="حذف"
        cancelText="لغو"
      />
      <div>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">مدیریت کاربران</h1>
            <p className="text-sm text-gray-500 mt-1">لیست تمام کاربران و مدیریت دسترسی‌ها</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-white shadow-sm transition-colors hover:bg-blue-700 sm:w-auto"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            افزودن کاربر جدید
          </button>
        </div>

        {error && !error.includes('دسترسی') && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <p className="text-sm text-gray-600">
              {filteredUsers.length.toLocaleString('fa-IR')} کاربر
              {searchQuery.trim() ? ` (از ${users.length.toLocaleString('fa-IR')})` : ''}
            </p>
            <div className="relative w-full sm:max-w-xs">
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="جستجو نام کاربری یا ایمیل..."
                className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2 pr-9 pl-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
              <svg
                className="pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>

          {filteredUsers.length === 0 ? (
            <p className="px-5 py-12 text-center text-sm text-gray-500">
              {users.length === 0 ? 'هیچ کاربری یافت نشد' : 'نتیجه‌ای برای جستجو پیدا نشد'}
            </p>
          ) : (
            <>
              {/* Mobile / tablet cards */}
              <div className="divide-y divide-gray-100 lg:hidden" style={{ direction: 'rtl' }}>
                {filteredUsers.map((user) => (
                  <article key={user.id} className="space-y-3 px-4 py-4 sm:px-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-base font-semibold text-gray-900">{user.username}</p>
                        <p className="mt-0.5 truncate text-sm text-gray-500" dir="ltr">
                          {user.email}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${getRoleBadgeColor(user.role)}`}
                      >
                        {getRoleLabel(user.role)}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {user.is_active ? 'فعال' : 'غیرفعال'}
                      </span>
                      <span className="text-xs text-gray-400">
                        ایجاد: {formatCreatedAt(user.created_at)}
                      </span>
                    </div>

                    <div>
                      <p className="mb-1.5 text-xs font-medium text-gray-500">دسترسی‌ها</p>
                      {renderPermissionBadges(user, true)}
                    </div>

                    {renderUserActions(user)}
                  </article>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden overflow-x-auto lg:block">
                <table className="min-w-full divide-y divide-gray-200" style={{ direction: 'rtl' }}>
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                        کاربر
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                        نقش
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                        دسترسی‌ها
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                        وضعیت
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                        تاریخ ایجاد
                      </th>
                      <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                        عملیات
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50/80">
                        <td className="px-5 py-4">
                          <p className="text-sm font-semibold text-gray-900">{user.username}</p>
                          <p className="mt-0.5 text-xs text-gray-500" dir="ltr">
                            {user.email}
                          </p>
                        </td>
                        <td className="whitespace-nowrap px-4 py-4">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getRoleBadgeColor(user.role)}`}
                          >
                            {getRoleLabel(user.role)}
                          </span>
                        </td>
                        <td className="px-4 py-4">{renderPermissionBadges(user)}</td>
                        <td className="whitespace-nowrap px-4 py-4">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                              user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {user.is_active ? 'فعال' : 'غیرفعال'}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-sm tabular-nums text-gray-500">
                          {formatCreatedAt(user.created_at)}
                        </td>
                        <td className="whitespace-nowrap px-5 py-4">{renderUserActions(user)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Add Modal */}
        {showAddModal && (
          <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-gray-900/50 p-4 sm:items-center">
            <div
              className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border bg-white p-5 shadow-xl sm:p-6"
              style={{ direction: 'rtl' }}
            >
                <h3 className="mb-4 text-lg font-bold text-gray-900">افزودن کاربر جدید</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">نام کاربری</label>
                    <input
                      type="text"
                      value={newUsername}
                      onChange={(e) => setNewUsername(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ایمیل</label>
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">رمز عبور</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">نقش</label>
                    <select
                      value={newRole}
                      onChange={(e) => setNewRole(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="expert">کارشناس</option>
                      <option value="manager">مدیر</option>
                      <option value="admin">مدیر اصلی</option>
                    </select>
                  </div>
                  {isMainAdminViewer && (
                    <div className="flex items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      <input
                        type="checkbox"
                        id="newManagementReportAccess"
                        checked={newManagementReportAccess}
                        onChange={(e) => setNewManagementReportAccess(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="newManagementReportAccess" className="mr-2 block text-sm text-gray-800 leading-snug">
                        دسترسی به گزارش مدیریتی (برای نقش‌های غیر مدیر اصلی)
                      </label>
                    </div>
                  )}
                  {isMainAdminViewer && (
                    <div className="flex items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      <input
                        type="checkbox"
                        id="newComplaintsAccess"
                        checked={newComplaintsAccess}
                        onChange={(e) => setNewComplaintsAccess(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="newComplaintsAccess" className="mr-2 block text-sm text-gray-800 leading-snug">
                        دسترسی به بخش انتقادات و پیشنهادات (برای نقش‌های غیر مدیر اصلی)
                      </label>
                    </div>
                  )}
                  {isMainAdminViewer && (
                    <div className="flex items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      <input
                        type="checkbox"
                        id="newMonitoringAccess"
                        checked={newMonitoringAccess}
                        onChange={(e) => setNewMonitoringAccess(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="newMonitoringAccess" className="mr-2 block text-sm text-gray-800 leading-snug">
                        دسترسی به بخش مانیتورینگ (برای نقش‌های غیر مدیر اصلی)
                      </label>
                    </div>
                  )}
                  {isMainAdminViewer && (
                    <div className="flex items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      <input
                        type="checkbox"
                        id="newAmiAccess"
                        checked={newAmiAccess}
                        onChange={(e) => setNewAmiAccess(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="newAmiAccess" className="mr-2 block text-sm text-gray-800 leading-snug">
                        دسترسی به بخش مرکز تلفن (AMI) (برای نقش‌های غیر مدیر اصلی)
                      </label>
                    </div>
                  )}
                  {isMainAdminViewer && (
                    <div className="flex items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      <input
                        type="checkbox"
                        id="newCallCenterReportAccess"
                        checked={newCallCenterReportAccess}
                        onChange={(e) => setNewCallCenterReportAccess(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="newCallCenterReportAccess" className="mr-2 block text-sm text-gray-800 leading-snug">
                        دسترسی به گزارش مرکز تماس (برای نقش‌های غیر مدیر اصلی)
                      </label>
                    </div>
                  )}
                  {isMainAdminViewer && (
                    <div className="flex items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      <input
                        type="checkbox"
                        id="newHisReportAccess"
                        checked={newHisReportAccess}
                        onChange={(e) => setNewHisReportAccess(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="newHisReportAccess" className="mr-2 block text-sm text-gray-800 leading-snug">
                        دسترسی به {HIS_MENU_TITLE} (برای نقش‌های غیر مدیر اصلی)
                      </label>
                    </div>
                  )}
                  {isMainAdminViewer && (
                    <div className="flex items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      <input
                        type="checkbox"
                        id="newCustomerClubAccess"
                        checked={newCustomerClubAccess}
                        onChange={(e) => setNewCustomerClubAccess(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="newCustomerClubAccess" className="mr-2 block text-sm text-gray-800 leading-snug">
                        دسترسی به باشگاه مشتریان (برای نقش‌های غیر مدیر اصلی)
                      </label>
                    </div>
                  )}
                  {isMainAdminViewer && (
                    <div className="flex items-center rounded-md border border-violet-200 bg-violet-50 px-3 py-2">
                      <input
                        type="checkbox"
                        id="newCustomerClubSegmentsAccess"
                        checked={newCustomerClubSegmentsAccess}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setNewCustomerClubSegmentsAccess(on);
                          if (on) setNewCustomerClubAccess(true);
                        }}
                        className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="newCustomerClubSegmentsAccess"
                        className="mr-2 block text-sm text-gray-800 leading-snug"
                      >
                        دسترسی به تنظیم سگمنت‌های باشگاه مشتریان
                      </label>
                    </div>
                  )}
                  {isMainAdminViewer && (
                    <div className="flex items-center rounded-md border border-teal-200 bg-teal-50 px-3 py-2">
                      <input
                        type="checkbox"
                        id="newScreenPopAccess"
                        checked={newScreenPopAccess}
                        onChange={(e) => setNewScreenPopAccess(e.target.checked)}
                        className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                      />
                      <label htmlFor="newScreenPopAccess" className="mr-2 block text-sm text-gray-800 leading-snug">
                        نمایش پاپ‌آپ تماس ورودی (شناسایی بیمار با شماره) — فعلاً برای تست دستی
                      </label>
                    </div>
                  )}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="newIsActive"
                      checked={newIsActive}
                      onChange={(e) => setNewIsActive(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="newIsActive" className="mr-2 block text-sm text-gray-900">
                      فعال
                    </label>
                  </div>
                </div>
                <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setNewUsername('');
                      setNewEmail('');
                      setNewPassword('');
                      setNewRole('expert');
                      setNewManagementReportAccess(false);
                      setNewComplaintsAccess(false);
                      setNewMonitoringAccess(false);
                      setNewAmiAccess(false);
      setNewCallCenterReportAccess(false);
      setNewHisReportAccess(false);
                      setNewScreenPopAccess(false);
                      setNewCustomerClubAccess(false);
                      setNewCustomerClubSegmentsAccess(false);
                      setNewIsActive(true);
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    انصراف
                  </button>
                  <button
                    onClick={handleAddUser}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    افزودن
                  </button>
                </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && editingUser && (
          <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-gray-900/50 p-4 sm:items-center">
            <div
              className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border bg-white p-5 shadow-xl sm:p-6"
              style={{ direction: 'rtl' }}
            >
                <h3 className="mb-4 text-lg font-bold text-gray-900">ویرایش کاربر</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">نام کاربری</label>
                    <input
                      type="text"
                      value={editUsername}
                      onChange={(e) => setEditUsername(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ایمیل</label>
                    <input
                      type="email"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">رمز عبور جدید (اختیاری)</label>
                    <input
                      type="password"
                      value={editPassword}
                      onChange={(e) => setEditPassword(e.target.value)}
                      placeholder="در صورت عدم تغییر خالی بگذارید"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">نقش</label>
                    <select
                      value={editRole}
                      onChange={(e) => setEditRole(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="expert">کارشناس</option>
                      <option value="manager">مدیر</option>
                      <option value="admin">مدیر اصلی</option>
                    </select>
                  </div>
                  {isMainAdminViewer && (
                    <div className="flex items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      <input
                        type="checkbox"
                        id="editManagementReportAccess"
                        checked={editManagementReportAccess}
                        onChange={(e) => setEditManagementReportAccess(e.target.checked)}
                        disabled={editRole === 'admin' || editRole === 'superadmin'}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-40"
                      />
                      <label htmlFor="editManagementReportAccess" className="mr-2 block text-sm text-gray-800 leading-snug">
                        دسترسی به گزارش مدیریتی
                        {(editRole === 'admin' || editRole === 'superadmin') && (
                          <span className="block text-xs text-gray-500 mt-0.5">برای مدیر اصلی همیشه فعال است</span>
                        )}
                      </label>
                    </div>
                  )}
                  {isMainAdminViewer && (
                    <div className="flex items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      <input
                        type="checkbox"
                        id="editComplaintsAccess"
                        checked={editComplaintsAccess}
                        onChange={(e) => setEditComplaintsAccess(e.target.checked)}
                        disabled={editRole === 'admin' || editRole === 'superadmin'}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-40"
                      />
                      <label htmlFor="editComplaintsAccess" className="mr-2 block text-sm text-gray-800 leading-snug">
                        دسترسی به بخش انتقادات و پیشنهادات
                        {(editRole === 'admin' || editRole === 'superadmin') && (
                          <span className="block text-xs text-gray-500 mt-0.5">برای مدیر اصلی همیشه فعال است</span>
                        )}
                      </label>
                    </div>
                  )}
                  {isMainAdminViewer && (
                    <div className="flex items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      <input
                        type="checkbox"
                        id="editMonitoringAccess"
                        checked={editMonitoringAccess}
                        onChange={(e) => setEditMonitoringAccess(e.target.checked)}
                        disabled={editRole === 'admin' || editRole === 'superadmin'}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-40"
                      />
                      <label htmlFor="editMonitoringAccess" className="mr-2 block text-sm text-gray-800 leading-snug">
                        دسترسی به بخش مانیتورینگ
                        {(editRole === 'admin' || editRole === 'superadmin') && (
                          <span className="block text-xs text-gray-500 mt-0.5">برای مدیر اصلی همیشه فعال است</span>
                        )}
                      </label>
                    </div>
                  )}
                  {isMainAdminViewer && (
                    <div className="flex items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      <input
                        type="checkbox"
                        id="editAmiAccess"
                        checked={editAmiAccess}
                        onChange={(e) => setEditAmiAccess(e.target.checked)}
                        disabled={editRole === 'admin' || editRole === 'superadmin'}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-40"
                      />
                      <label htmlFor="editAmiAccess" className="mr-2 block text-sm text-gray-800 leading-snug">
                        دسترسی به بخش مرکز تلفن (AMI)
                        {(editRole === 'admin' || editRole === 'superadmin') && (
                          <span className="block text-xs text-gray-500 mt-0.5">برای مدیر اصلی همیشه فعال است</span>
                        )}
                      </label>
                    </div>
                  )}
                  {isMainAdminViewer && (
                    <div className="flex items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      <input
                        type="checkbox"
                        id="editCallCenterReportAccess"
                        checked={editCallCenterReportAccess}
                        onChange={(e) => setEditCallCenterReportAccess(e.target.checked)}
                        disabled={editRole === 'admin' || editRole === 'superadmin'}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-40"
                      />
                      <label htmlFor="editCallCenterReportAccess" className="mr-2 block text-sm text-gray-800 leading-snug">
                        دسترسی به گزارش مرکز تماس
                        {(editRole === 'admin' || editRole === 'superadmin') && (
                          <span className="block text-xs text-gray-500 mt-0.5">برای مدیر اصلی همیشه فعال است</span>
                        )}
                      </label>
                    </div>
                  )}
                  {isMainAdminViewer && (
                    <div className="flex items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      <input
                        type="checkbox"
                        id="editHisReportAccess"
                        checked={editHisReportAccess}
                        onChange={(e) => setEditHisReportAccess(e.target.checked)}
                        disabled={editRole === 'admin' || editRole === 'superadmin'}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-40"
                      />
                      <label htmlFor="editHisReportAccess" className="mr-2 block text-sm text-gray-800 leading-snug">
                        دسترسی به {HIS_MENU_TITLE}
                        {(editRole === 'admin' || editRole === 'superadmin') && (
                          <span className="block text-xs text-gray-500 mt-0.5">برای مدیر اصلی همیشه فعال است</span>
                        )}
                      </label>
                    </div>
                  )}
                  {isMainAdminViewer && (
                    <div className="flex items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      <input
                        type="checkbox"
                        id="editCustomerClubAccess"
                        checked={editCustomerClubAccess}
                        onChange={(e) => setEditCustomerClubAccess(e.target.checked)}
                        disabled={editRole === 'admin' || editRole === 'superadmin'}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-40"
                      />
                      <label htmlFor="editCustomerClubAccess" className="mr-2 block text-sm text-gray-800 leading-snug">
                        دسترسی به باشگاه مشتریان
                        {(editRole === 'admin' || editRole === 'superadmin') && (
                          <span className="block text-xs text-gray-500 mt-0.5">برای مدیر اصلی همیشه فعال است</span>
                        )}
                      </label>
                    </div>
                  )}
                  {isMainAdminViewer && (
                    <div className="flex items-center rounded-md border border-violet-200 bg-violet-50 px-3 py-2">
                      <input
                        type="checkbox"
                        id="editCustomerClubSegmentsAccess"
                        checked={editCustomerClubSegmentsAccess}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setEditCustomerClubSegmentsAccess(on);
                          if (on) setEditCustomerClubAccess(true);
                        }}
                        disabled={editRole === 'admin' || editRole === 'superadmin'}
                        className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded disabled:opacity-40"
                      />
                      <label
                        htmlFor="editCustomerClubSegmentsAccess"
                        className="mr-2 block text-sm text-gray-800 leading-snug"
                      >
                        دسترسی به تنظیم سگمنت‌های باشگاه مشتریان
                        {(editRole === 'admin' || editRole === 'superadmin') && (
                          <span className="block text-xs text-gray-500 mt-0.5">برای مدیر اصلی همیشه فعال است</span>
                        )}
                      </label>
                    </div>
                  )}
                  {isMainAdminViewer && (
                    <div className="flex items-center rounded-md border border-teal-200 bg-teal-50 px-3 py-2">
                      <input
                        type="checkbox"
                        id="editScreenPopAccess"
                        checked={editScreenPopAccess}
                        onChange={(e) => setEditScreenPopAccess(e.target.checked)}
                        className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                      />
                      <label htmlFor="editScreenPopAccess" className="mr-2 block text-sm text-gray-800 leading-snug">
                        نمایش پاپ‌آپ تماس ورودی (شناسایی بیمار با شماره)
                        <span className="block text-xs text-gray-500 mt-0.5">
                          حتی برای مدیر اصلی باید دستی فعال شود (برای تست)
                        </span>
                      </label>
                    </div>
                  )}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="editIsActive"
                      checked={editIsActive}
                      onChange={(e) => setEditIsActive(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="editIsActive" className="mr-2 block text-sm text-gray-900">
                      فعال
                    </label>
                  </div>
                </div>
                <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingUser(null);
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    انصراف
                  </button>
                  <button
                    onClick={handleUpdateUser}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    ذخیره
                  </button>
                </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;

